import { motion } from 'framer-motion'
import { useLocation } from 'react-router-dom'

// Transitions modernes iOS-like avec effets fluides
const getPageVariants = (pathname) => {
  // Pages d'administration - transition subtile et professionnelle
  if (pathname.startsWith('/admin')) {
    return {
      initial: { opacity: 0, y: 15, scale: 0.98 },
      in: { opacity: 1, y: 0, scale: 1 },
      out: { opacity: 0, y: -15, scale: 0.98 }
    }
  }
  
  // Pages de produit - transition avec zoom et rotation subtile
  if (pathname.startsWith('/catalogue-location-materiel-tournage-guadeloupe/')) {
    return {
      initial: { opacity: 0, scale: 0.9, y: 30, rotateX: -10 },
      in: { opacity: 1, scale: 1, y: 0, rotateX: 0 },
      out: { opacity: 0, scale: 1.1, y: -30, rotateX: 10 }
    }
  }
  
  // Pages de checkout/panier - transition latérale fluide
  if (pathname.includes('panier') || pathname.includes('checkout')) {
    return {
      initial: { opacity: 0, x: 50, scale: 0.95 },
      in: { opacity: 1, x: 0, scale: 1 },
      out: { opacity: 0, x: -50, scale: 0.95 }
    }
  }
  
  // Pages catalogue - transition avec effet de profondeur
  if (pathname.includes('catalogue') || pathname.includes('rentman')) {
    return {
      initial: { opacity: 0, y: 40, scale: 0.9, rotateY: -5 },
      in: { opacity: 1, y: 0, scale: 1, rotateY: 0 },
      out: { opacity: 0, y: -40, scale: 0.9, rotateY: 5 }
    }
  }
  
  // Pages d'authentification - transition élégante
  if (pathname.includes('login') || pathname.includes('register') || pathname.includes('forgot')) {
    return {
      initial: { opacity: 0, y: 25, scale: 0.95, filter: 'blur(4px)' },
      in: { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' },
      out: { opacity: 0, y: -25, scale: 0.95, filter: 'blur(4px)' }
    }
  }
  
  // Transition par défaut - iOS-like moderne
  return {
    initial: { 
      opacity: 0, 
      x: 30, 
      y: 20,
      scale: 0.95,
      filter: 'blur(3px)'
    },
    in: { 
      opacity: 1, 
      x: 0, 
      y: 0,
      scale: 1,
      filter: 'blur(0px)'
    },
    out: { 
      opacity: 0, 
      x: -30, 
      y: -20,
      scale: 0.95,
      filter: 'blur(3px)'
    }
  }
}

const getPageTransition = (pathname) => {
  // Pages d'administration - transition rapide et professionnelle
  if (pathname.startsWith('/admin')) {
    return {
      type: 'tween',
      ease: [0.4, 0.0, 0.2, 1], // Material Design easeOut
      duration: 0.25
    }
  }
  
  // Pages de produit - transition fluide avec rebond subtil
  if (pathname.startsWith('/catalogue-location-materiel-tournage-guadeloupe/')) {
    return {
      type: 'tween',
      ease: [0.25, 0.1, 0.25, 1], // iOS easeOut
      duration: 0.5
    }
  }
  
  // Pages de checkout/panier - transition latérale fluide
  if (pathname.includes('panier') || pathname.includes('checkout')) {
    return {
      type: 'tween',
      ease: [0.4, 0.0, 0.2, 1], // Material Design easeOut
      duration: 0.4
    }
  }
  
  // Pages catalogue - transition avec effet de profondeur
  if (pathname.includes('catalogue') || pathname.includes('rentman')) {
    return {
      type: 'tween',
      ease: [0.25, 0.1, 0.25, 1], // iOS easeOut
      duration: 0.45
    }
  }
  
  // Pages d'authentification - transition élégante
  if (pathname.includes('login') || pathname.includes('register') || pathname.includes('forgot')) {
    return {
      type: 'tween',
      ease: [0.4, 0.0, 0.2, 1], // Material Design easeOut
      duration: 0.4
    }
  }
  
  // Transition par défaut - iOS-like moderne
  return {
    type: 'tween',
    ease: [0.25, 0.1, 0.25, 1], // iOS easeOut - courbe naturelle
    duration: 0.4
  }
}

const PageTransition = ({ children }) => {
  const location = useLocation()
  const variants = getPageVariants(location.pathname)
  const transition = getPageTransition(location.pathname)

  return (
    <motion.div
      key={location.pathname}
      initial="initial"
      animate="in"
      exit="out"
      variants={variants}
      transition={transition}
      className="min-h-screen"
      style={{ 
        willChange: 'transform, opacity, filter',
        backfaceVisibility: 'hidden',
        perspective: 1000
      }}
      // Optimisations de performance
      layout={false}
      transformTemplate={({ x, y, scale, rotateX, rotateY, filter }) => 
        `translate3d(${x}, ${y}, 0) scale(${scale}) rotateX(${rotateX}deg) rotateY(${rotateY}deg) ${filter}`
      }
    >
      {children}
    </motion.div>
  )
}

export default PageTransition
